import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useAtsSyncStream, { ACTIVE_SYNC_STORAGE_KEY } from './hooks/useAtsSyncStream';

const POLL_INTERVAL_MS = 1000;

// Mounted once at the app root so the sync-started/sync-complete toast fires regardless of which
// page the user is on. Reuses the same per-sync SSE stream and sessionStorage key that
// ParsingStatusAlert already relies on — no separate company-wide notification channel, no
// backend changes. sessionStorage doesn't emit a same-tab "storage" event, so a short poll is the
// simplest reliable way to notice a sync starting from wherever the user happens to be.
export default function AtsSyncToastListener() {
  const [syncConfigId, setSyncConfigId] = useState<string | null>(() =>
    sessionStorage.getItem(ACTIVE_SYNC_STORAGE_KEY),
  );
  const shownToastIds = useRef(new Set<string>());

  useEffect(() => {
    const interval = setInterval(() => {
      setSyncConfigId(sessionStorage.getItem(ACTIVE_SYNC_STORAGE_KEY));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const { isCompleted, jobsCreated, totalJobs, candidatesSynced, failedJobs } = useAtsSyncStream(
    syncConfigId,
    !!syncConfigId,
  );

  const showOnce = (toastId: string, render: () => void) => {
    if (shownToastIds.current.has(toastId)) return;
    shownToastIds.current.add(toastId);
    render();
  };

  useEffect(() => {
    if (!syncConfigId) return;
    showOnce(`${syncConfigId}-started`, () =>
      toast.info(
        <div>
          <div className="fw-semibold">ATS sync started</div>
          <div>You can continue using Interview Agent. We&apos;ll notify you once the sync is complete.</div>
        </div>,
        { toastId: `${syncConfigId}-started` },
      ),
    );
  }, [syncConfigId]);

  useEffect(() => {
    if (!syncConfigId || !isCompleted) return;
    const failedCount = failedJobs.length;
    // totalJobs counts failed jobs too (same as ParsingStatusAlert's banner), so subtract them.
    const jobsSynced = Math.max((totalJobs || jobsCreated) - failedCount, 0);
    const candidates = candidatesSynced ?? 0;
    const failedText = `${failedCount} job${failedCount !== 1 ? 's' : ''}`;
    showOnce(`${syncConfigId}-finished`, () => {
      if (failedCount > 0 && jobsSynced === 0) {
        toast.error(
          <div>
            <div className="fw-semibold">Sync failed</div>
            <div>{failedText} couldn&apos;t be synced.</div>
          </div>,
          { toastId: `${syncConfigId}-finished` },
        );
        return;
      }
      const syncedText = (
        <>
          {jobsSynced} job{jobsSynced !== 1 ? 's' : ''} and {candidates} candidate resume
          {candidates !== 1 ? 's' : ''} were synced successfully.
        </>
      );
      if (failedCount > 0) {
        toast.warning(
          <div>
            <div className="fw-semibold">Sync completed with errors</div>
            <div>
              {syncedText} {failedText} couldn&apos;t be synced.
            </div>
          </div>,
          { toastId: `${syncConfigId}-finished` },
        );
        return;
      }
      toast.success(
        <div>
          <div className="fw-semibold">Sync complete</div>
          <div>{syncedText}</div>
        </div>,
        { toastId: `${syncConfigId}-finished` },
      );
    });
  }, [syncConfigId, isCompleted, totalJobs, jobsCreated, candidatesSynced, failedJobs]);

  return null;
}
